import { z } from 'zod';
/**
 * Der Vertrag der gespeicherten Tabellenansicht (Chip `V-belegfilter`).
 *
 * Eine Ansicht ist das, was ein Benutzer an einer Liste eingestellt hat:
 * **Spaltenfilter**, **Spaltensichtbarkeit** und **Spaltenreihenfolge**. Sie
 * liegt je Benutzer und je Tabelle in der Datenbank (`user_table_view`,
 * Migration 0066) und nicht im Browser — eine Einstellung im `localStorage`
 * gehört einem Gerät und nicht einer Person.
 *
 * **Die Feldnamen sind ENGLISCH, und das ist keine Nachlässigkeit.** Sie sind
 * wörtlich die zweier fremder Vokabulare, die diese Oberfläche benutzt:
 * `filters` trägt das Modell von **bazza/ui data-table-filter** (`columnId`,
 * `type`, `operator`, `values`), `columnVisibility` und `columnOrder` sind die
 * Namen von **TanStack Table**. Eine Übersetzung an dieser Stelle wäre eine
 * dritte Schreibweise für dieselbe Sache — und jede Stelle, die zwischen den
 * Bibliotheken und dem Speicher vermittelt, müsste sie hin und zurück
 * übersetzen. Was der Vertrag SAGT, steht auf Deutsch: in den Kommentaren.
 *
 * **`v` ist der Grund, aus dem dieser Vertrag existiert.** Eine Ansicht ist
 * Oberflächenzustand, und Oberflächen ändern sich: Eine Spalte fällt weg, ein
 * Operator wird umbenannt, ein Filtertyp kommt hinzu. Eine gespeicherte Ansicht
 * aus der Zeit davor ist dann nicht falsch, sondern **fremd**. Sie wird deshalb
 * **verworfen und nicht gedeutet** — `leseAnsicht()` gibt `undefined` zurück,
 * und die Liste steht wie bei einem Benutzer, der noch nie etwas eingestellt
 * hat. Das ist die ganze Zusage: Ein Formatwechsel kostet eine Einstellung und
 * niemals eine kaputte Maske.
 *
 * **Der Preis dieser Entscheidung, ausdrücklich:** Wer `v` erhöht, wirft die
 * Ansichten aller Benutzer weg. Das ist bei einem Merkzettel richtig und wäre
 * bei einem Beleg falsch — deshalb steht diese Bauform hier und nirgends sonst.
 */
/**
 * Die Fassung, die diese Anwendung schreibt und liest.
 *
 * Eine Zahl und keine Zeichenkette: Sie wird verglichen und nie geordnet.
 */
export const TABELLENANSICHT_VERSION = 1;
/**
 * Die Datentypen einer filterbaren Spalte — die Teilmenge von bazza/ui, die
 * dieser Einbau wirklich trägt.
 *
 * **`number` und `multiOption` fehlen mit Absicht.** bazza kennt beide; ihre
 * Bedienelemente sind ein Schieberegler und eine Mehrfachauswahl, und beide
 * bringen in der Vorlage Fremdabhängigkeiten mit, die dieses Projekt nicht hat
 * (Radix `slider`, `tabs`, `command`). Was hier nicht steht, kann auch nicht
 * gespeichert werden — ein Vertrag, der Typen führt, für die es kein
 * Bedienelement gibt, beschreibt einen Zustand, den niemand herstellen kann.
 *
 * Ein Betrag ist deshalb hier **kein** `number`-Filter: Geld ist in diesem
 * Projekt niemals `number` (Hausregel 1), und ein Schieberegler über Beträge
 * wäre der kürzeste Weg zu einem Rundungsfehler in einer Suchmaske.
 */
export const SPALTENTYPEN = ['text', 'date', 'option'];
/** Operatoren für Textspalten. Wörtlich die von bazza/ui. */
export const TEXT_OPERATOREN = ['contains', 'does not contain'];
/** Operatoren für Datumsspalten. Die Teilmenge ohne Zeitraum-Bedienelement. */
export const DATE_OPERATOREN = [
    'is',
    'is not',
    'is before',
    'is on or after',
    'is after',
    'is on or before',
];
/** Operatoren für Auswahlspalten. */
export const OPTION_OPERATOREN = ['is', 'is not', 'is any of', 'is none of'];
/**
 * Alle Operatoren, die dieser Vertrag kennt.
 *
 * Ausgeschrieben und nicht aus den drei Listen darüber zusammengesetzt: `z.enum`
 * verlangt ein Tupel aus Zeichenkettenliteralen, und ein `[...a, ...b]` liefert
 * dem Übersetzer davon nur noch die Vereinigung der Elementtypen — der Vertrag
 * verlöre genau die Literale, um derentwillen er hier steht. Die Gegenprobe
 * gegen die drei Listen leistet ein Komponententest, nicht der Blick.
 */
export const FILTER_OPERATOREN = [
    'contains',
    'does not contain',
    'is',
    'is not',
    'is before',
    'is on or after',
    'is after',
    'is on or before',
    'is any of',
    'is none of',
];
/**
 * Ein einzelner Spaltenfilter.
 *
 * **`values` ist immer eine Liste, auch bei einem einzelnen Wert.** Das ist
 * bazzas Modell und nicht bequem gedacht: Derselbe Filter wechselt zwischen
 * «ist» und «ist eines von», ohne die Form seines Wertes zu ändern — und ein
 * Feld, das mal Wert und mal Liste ist, ist die klassische Stelle, an der eine
 * gespeicherte Ansicht beim Zurücklesen bricht.
 *
 * **Die Werte sind Zeichenketten und nichts anderes.** Ein Datum steht als
 * `YYYY-MM-DD` darin, eine Auswahl als ihr Schlüssel. Ein `Date` durch JSON zu
 * schicken hiesse, es über die Zeitzone des Browsers zu schicken; ein Filter
 * «ausgestellt am 2026-08-25», der beim Zurücklesen einen Tag springt, ist
 * schlimmer als kein Filter.
 *
 * **Die Paarung von `type` und `operator` prüft dieses Schema NICHT** — ein
 * `{ type: 'text', operator: 'is before' }` kommt hier durch. Sie zu prüfen
 * wäre möglich (ein `discriminatedUnion`) und wäre trotzdem falsch: Welche
 * Spalte welchen Typ hat, weiss allein die Liste, die die Ansicht anzeigt, und
 * sie ändert es, ohne dass ein Vertrag davon erfährt. Die Prüfung, die wirklich
 * trägt, ist deshalb die beim **Anwenden** (`apps/web/src/ui/tabellenfilter/`):
 * Ein Filter auf eine Spalte, die es nicht mehr gibt, oder mit einem Operator,
 * den ihr Typ nicht kennt, wird dort still fallengelassen. Ein Vertrag, der
 * hier streng wäre, verwürfe stattdessen die GANZE Ansicht wegen einer Spalte.
 */
export const filterModelSchema = z.object({
    columnId: z.string().min(1).max(100),
    type: z.enum(SPALTENTYPEN),
    operator: z.enum(FILTER_OPERATOREN),
    values: z.array(z.string().max(200)).max(50),
});
/**
 * Die vollständige Ansicht einer Tabelle.
 *
 * **`columnVisibility` führt nur die ABWEICHUNGEN**, nicht jede Spalte. Eine
 * Spalte, die nicht darin steht, ist sichtbar — das ist TanStacks eigene
 * Auslegung und zugleich die, die den Standardfall billig macht: Ein Benutzer,
 * der nichts ausgeblendet hat, speichert ein leeres Objekt und sieht damit
 * automatisch jede Spalte, die später dazukommt.
 *
 * **`columnOrder` darf unvollständig sein und Unbekanntes enthalten.** Eine
 * gespeicherte Reihenfolge, die eine inzwischen entfernte Spalte nennt, ist
 * kein Grund, die Ansicht wegzuwerfen; die Anwendung sortiert danach, was sie
 * kennt, und hängt den Rest in der Vorgabereihenfolge an.
 */
/**
 * Eine Sortiervorgabe — Auftrag `W-belegsortierung`, 2026-09-10.
 *
 * **Wörtlich TanStack Tables `SortingState`-Eintrag**, `{ id, desc }`, wie
 * `columnVisibility` und `columnOrder` daneben wörtlich TanStacks Namen sind.
 * Eine eigene Schreibweise (`{ spalte, richtung: 'asc' | 'desc' }`) wäre eine
 * dritte Vokabel für dieselbe Sache, und jede Stelle zwischen Tabelle und
 * Speicher müsste sie hin und zurück übersetzen.
 *
 * **`desc: boolean` und kein dritter Wert für «gar nicht sortiert».** Der
 * Vorgabezustand ist die LEERE Liste — genau der Zustand, in den der dritte
 * Klick auf einen Spaltenkopf zurückführt (aufsteigend → absteigend →
 * Vorgabe). Ein Wert `'keine'` neben `asc`/`desc` wäre eine zweite Darstellung
 * desselben Zustands, und zwei Darstellungen eines Zustands sind zwei
 * Zustände.
 */
export const sortiereintragSchema = z.object({
    id: z.string().min(1).max(100),
    desc: z.boolean(),
});
export const tabellenansichtSchema = z.object({
    v: z.literal(TABELLENANSICHT_VERSION),
    filters: z.array(filterModelSchema).max(20),
    columnVisibility: z.record(z.string().max(100), z.boolean()),
    columnOrder: z.array(z.string().min(1).max(100)).max(100),
    /**
     * Die Sortierung — **optional und ohne neue Fassung** (`W-belegsortierung`,
     * 2026-09-10).
     *
     * **Das Feld ist nachgetragen, `v` bleibt bei 1, und das ist die
     * Entscheidung.** Eine erhöhte Fassung wirft nach dem Kopfkommentar oben die
     * Ansichten ALLER Benutzer weg — für ein zusätzliches, weglassbares Feld
     * wäre das der falsche Preis. Eine gespeicherte Ansicht aus der Zeit davor
     * ist hier nicht «fremd», sondern nur «ohne Sortierung», und genau so wird
     * sie gelesen: `undefined` heisst Vorgabeordnung, dasselbe wie die leere
     * Liste. Der Preis dieser Entscheidung, ausdrücklich: Ein ÄLTERER Klient,
     * der eine Ansicht MIT Sortierung zurückschreibt, verliert sie stillschweigend
     * — `z.object` ohne `.strict()` streift unbekannte Felder ab. Bei einem
     * Merkzettel ist das tragbar; bei einem Beleg wäre es es nicht.
     *
     * **Höchstens EIN Eintrag.** Diese Liste sortiert nach genau einer Spalte.
     * Die Form bleibt trotzdem eine Liste, weil es TanStacks Form ist: Eine
     * späte Mehrfachsortierung kostet dann eine geänderte Obergrenze und keinen
     * Formatwechsel — und ein Formatwechsel verwürfe die Ansichten aller
     * Benutzer.
     */
    sorting: z.array(sortiereintragSchema).max(1).optional(),
});
/**
 * Die leere Ansicht — was ein Benutzer sieht, der noch nie etwas eingestellt
 * hat, und wohin «Zurücksetzen» ihn zurückbringt.
 *
 * Eine **Funktion** und keine Konstante: Eine geteilte Konstante mit einem
 * Array darin wäre ein Objekt, das sich alle Aufrufer teilen, und der erste,
 * der `push` darauf ruft, ändert die Vorgabe für alle.
 */
export function leereAnsicht() {
    return { v: TABELLENANSICHT_VERSION, filters: [], columnVisibility: {}, columnOrder: [] };
}
/**
 * Liest eine gespeicherte Ansicht — oder gibt `undefined` zurück.
 *
 * **Das ist die Stelle, an der die Fassung wirkt.** Alles, was nicht genau der
 * heutigen Form entspricht — eine fremde `v`, ein fehlendes Feld, ein Operator,
 * den es nicht mehr gibt, ein Rumpf, der gar kein Objekt ist —, ergibt
 * `undefined` und keinen Fehler. Der Aufrufer zeigt dann die Vorgabeansicht.
 *
 * **Warum kein `throw`.** Diese Funktion läuft beim Laden einer Liste, und ein
 * Wurf an dieser Stelle machte aus einer verworfenen Einstellung eine leere
 * Seite. Der Ausfall ist ausdrücklich still: Eine Meldung «Ihre gespeicherte
 * Ansicht war zu alt» hilft niemandem, der sie in zwei Klicks neu einstellt.
 */
export function leseAnsicht(roh) {
    const geprueft = tabellenansichtSchema.safeParse(roh);
    return geprueft.success ? geprueft.data : undefined;
}
/**
 * Die Sachkonto-Präferenzen der Erfassungsmaske (Chip `V-favoritenpersist`) —
 * derselbe Speicher (`user_table_view`, Migration 0066), eine ANDERE Form.
 *
 * **Eigener Schema-Ast mit eigenem `v`, keine `discriminatedUnion` nach
 * Kennung.** Beide Bauformen standen zur Wahl. Eine `discriminatedUnion`
 * bräuchte ein Unterscheidungsfeld IM Rumpf (z. B. `kind: 'sachkonto-auswahl'`)
 * — eine zweite Quelle für dieselbe Tatsache, die der Pfad
 * `/me/tabellenansicht/:tabelle` schon trägt, und die beiden Quellen könnten
 * auseinanderlaufen (ein Rumpf mit `kind: 'belege'` unter dem Pfad
 * `sachkonto-auswahl` PUTten). Der Tabellenname aus dem Pfad ist bereits die
 * Kennung, unter der die Zeile in `user_table_view` liegt — sie ein zweites
 * Mal in den Rumpf zu legen, wäre die Art von Duplikat, an der schon
 * `filterModelSchema` (`type`/`operator`, siehe oben) bewusst NICHT
 * querprüft. Der eigene Ast lässt den Pfad die einzige Kennung bleiben:
 * `schemaFuerTabelle()` unten wählt danach, welcher Ast gilt, und jeder Ast
 * verwirft für sich, was seiner eigenen Fassung fremd ist.
 *
 * **Die Form ist bewusst dieselbe wie im bisherigen Modulzustand**
 * (`apps/web/src/eingangsrechnungen/sachkontoPraeferenzen.ts`, vor diesem
 * Chip nur `useSyncExternalStore` ohne Persistenz): `favoriten` und `zuletzt`,
 * beide Listen von Kontonummern. Der Umbau tauscht damit eine Datenquelle aus
 * und keine Struktur.
 */
export const SACHKONTO_PRAEFERENZEN_TABELLE = 'sachkonto-auswahl';
/**
 * Die Fassung, die diese Anwendung für die Sachkonto-Präferenzen schreibt und
 * liest — unabhängig von `TABELLENANSICHT_VERSION`. Ein Formatwechsel bei den
 * Spaltenansichten (`belege`) darf die Favoriten eines Benutzers nicht
 * mitreissen, und umgekehrt.
 */
export const SACHKONTO_PRAEFERENZEN_VERSION = 1;
/**
 * Wie viele zuletzt verwendete Sachkonten eine Zeile trägt.
 *
 * Wörtlich `MAX_ZULETZT` aus `sachkontoPraeferenzen.ts` — der Server rundet
 * hier nicht grosszügiger, als der Klient je schreiben würde; ein Rumpf mit
 * mehr Einträgen ist entweder ein alter Klient oder ein zweiter Schreibweg,
 * und beides verdient eine Ablehnung und keine stille Kürzung.
 */
const MAX_ZULETZT = 8;
/**
 * Eine einzelne Kontonummer, wie `SachkontoAuswahl.tsx` sie durchreicht
 * (`Account['number']`, `0002_stammdaten.sql`). SKR03-Kontonummern sind
 * kurz; die Grenze ist grosszügig und schützt vor einem Rumpf, der etwas
 * anderes als Kontonummern hineinschreibt.
 */
const kontonummerSchema = z.string().min(1).max(20);
export const sachkontoPraeferenzenSchema = z.object({
    v: z.literal(SACHKONTO_PRAEFERENZEN_VERSION),
    /**
     * Ohne Deckel in der Oberfläche — ein Benutzer darf so viele Sachkonten
     * favorisieren, wie er will. `200` bleibt weit über jedem SKR03-Auszug, den
     * ein Buchhalter tatsächlich anfasst, und hält die Zeile weit unter der
     * `16384`-Byte-Grenze aus Migration 0066.
     */
    favoriten: z.array(kontonummerSchema).max(200),
    zuletzt: z.array(kontonummerSchema).max(MAX_ZULETZT),
});
/** Die leeren Präferenzen — ein Benutzer, der noch nichts favorisiert hat. */
export function leereSachkontoPraeferenzen() {
    return { v: SACHKONTO_PRAEFERENZEN_VERSION, favoriten: [], zuletzt: [] };
}
/**
 * Liest gespeicherte Sachkonto-Präferenzen — oder gibt `undefined` zurück.
 *
 * Dieselbe Zusage wie `leseAnsicht()`: Eine fremde Fassung, ein fehlendes
 * Feld, ein Rumpf, der kein Objekt ist — alles ergibt `undefined` und keinen
 * Fehler. Der Aufrufer zeigt dann «noch keine Favoriten», nicht einen Absturz.
 */
export function leseSachkontoPraeferenzen(roh) {
    const geprueft = sachkontoPraeferenzenSchema.safeParse(roh);
    return geprueft.success ? geprueft.data : undefined;
}
/**
 * Der gemeinsame Ausgabetyp beider Äste, die `user_table_view.ansicht`
 * tragen kann — je nachdem, welche Tabelle gemeint ist.
 *
 * **Als Schema nachgetragen (`V-vertrag3`, 2026-08-29).** Bis dahin war
 * `Tabellenzustand` eine reine Vereinigung zweier bestehender Typen ohne
 * eigenes Schema; `GET /api/v1/me/tabellenansicht/:tabelle` löste seinen
 * Rückgabetyp `{ ansicht: Tabellenzustand | null }` deshalb nicht auf — ein
 * Objektliteral entsteht dem Erzeuger nur, wenn JEDES Feld sich auflösen
 * lässt (`apps/api/src/openapi/dokument.ts`, `objektLiteral`), und für
 * `Tabellenzustand` gab es keinen Vertrag unter diesem Namen
 * (`schemaNameZuTyp`). `tabellenzustandSchema` unten ist keine dritte Form,
 * sondern die Vereinigung der beiden vorhandenen Äste.
 */
export const tabellenzustandSchema = z.union([tabellenansichtSchema, sachkontoPraeferenzenSchema]);
/**
 * Die Antworthülle von `GET /api/v1/me/tabellenansicht/:tabelle` — `{ ansicht: ... | null }`. **Der Server weiss
 * nicht, welcher Ast gemeint ist**: Die Tabellenkennung im Pfad wählt ihn (`schemaFuerTabelle()`), die Antwort trägt
 * deshalb die Vereinigung beider Äste, und `null` heisst «nichts gespeichert» — nicht 404. Die beiden Schemas
 * darunter sind dieselbe Hülle, auf den Ast verengt, den die aufrufende Seite über die Kennung bereits gewählt hat
 * (Handbuch §3.969).
 */
export const tabellenzustandAntwortSchema = z.object({ ansicht: tabellenzustandSchema.nullable() }).strict();
/** Dieselbe Hülle, verengt auf die Spaltenansicht (`belege` und jede Kennung ausser `sachkonto-auswahl`). */
export const tabellenansichtAntwortSchema = z.object({ ansicht: tabellenansichtSchema.nullable() }).strict();
/** Dieselbe Hülle, verengt auf die Sachkonto-Präferenzen (Kennung `sachkonto-auswahl`). */
export const sachkontoPraeferenzenAntwortSchema = z.object({ ansicht: sachkontoPraeferenzenSchema.nullable() }).strict();
/**
 * Wählt den Schema-Ast nach der Tabellenkennung aus dem Pfad.
 *
 * **Die einzige Verzweigungsstelle.** Controller und Dienst rufen nur diese
 * Funktion und nie `tabellenansichtSchema` oder `sachkontoPraeferenzenSchema`
 * direkt — sonst entstünde eine zweite Stelle, die wissen müsste, welche
 * Tabelle welche Form trägt, und die beiden liefen irgendwann auseinander.
 * Jede Tabellenkennung ausser `sachkonto-auswahl` bekommt weiterhin die
 * Spaltenansicht — das ist genau das Verhalten von vor diesem Chip und bleibt
 * für `belege` unverändert.
 */
export function schemaFuerTabelle(tabelle) {
    return tabelle === SACHKONTO_PRAEFERENZEN_TABELLE ? sachkontoPraeferenzenSchema : tabellenansichtSchema;
}
/**
 * Liest eine gespeicherte Zeile aus `user_table_view` — Ansicht oder
 * Präferenzen, je nach `tabelle` — oder gibt `undefined` zurück.
 *
 * Dieselbe stille Zusage wie `leseAnsicht()` und `leseSachkontoPraeferenzen()`,
 * nur mit der Verzweigung davor.
 */
export function leseTabellenzustand(roh, tabelle) {
    const geprueft = schemaFuerTabelle(tabelle).safeParse(roh);
    return geprueft.success ? geprueft.data : undefined;
}
//# sourceMappingURL=tabellenansicht.js.map